import * as SheetPrimitive from "@radix-ui/react-dialog"
import { cva, type VariantProps } from "class-variance-authority"
import { X } from "lucide-react"
import * as React from "react"

import { cn } from "@/lib/utils"

const Sheet = SheetPrimitive.Root

const SheetTrigger = SheetPrimitive.Trigger

const SheetClose = SheetPrimitive.Close

const SheetPortal = SheetPrimitive.Portal

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
    ref={ref}
  />
))
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName

const sheetVariants = cva(
  "fixed z-50 gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
        right:
          "inset-y-0 right-0 h-full w-3/4  border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm",
      },
    },
    defaultVariants: {
      side: "right",
    },
  }
)

/**
 * Floating (inset) drawer variant — a rounded panel that hovers just inside the
 * viewport edge (12px inset all round) over a soft blurred scrim, matching the
 * V3 design language. Only left/right docking is supported; the panel manages
 * its own padding, so consumers style the body via className (e.g. `p-0`).
 *
 * Deliberately NO `overflow-hidden` here (unlike earlier versions of this
 * variant) — a resize-handle grip is meant to float partly outside the panel
 * over the scrim (see `FloatingDrawer`'s `resizeHandle` prop / `LeadDetailView`'s
 * own handle), and `overflow-hidden` on this element would clip ANY
 * descendant positioned outside its box, no matter how deep in the tree —
 * cutting that grip in half. `border-radius` still paints this panel's own
 * rounded shape without it; only content clipping needs the inner wrapper
 * each consumer now provides around its own children instead.
 */
const floatingBase =
  "fixed z-50 flex flex-col top-3 bottom-3 rounded-2xl border border-[#E4E7EC] bg-background shadow-[0_20px_50px_rgba(16,24,40,0.18)] max-w-[calc(100vw-24px)] transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500 focus:outline-none"

const floatingSide: Record<"left" | "right", string> = {
  right:
    "right-3 data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
  left: "left-3 data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left",
}

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
  VariantProps<typeof sheetVariants> {
  /** Hide the built-in top-right close button (for content that renders its own). */
  hideClose?: boolean
  /**
   * Render the floating (inset) drawer variant instead of the flush edge-to-edge
   * sheet: rounded panel inset 12px from the edge over a blurred scrim. Only
   * `left`/`right` sides apply. See the V3 design-system "Floating drawer" tab.
   */
  floating?: boolean
}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(({ side = "right", className, children, hideClose = false, floating = false, ...props }, ref) => (
  <SheetPortal>
    {floating ? (
      <SheetOverlay
        data-floating-drawer-overlay=""
        className="bg-[#101828]/30 backdrop-blur-[2px]"
      />
    ) : (
      <SheetOverlay />
    )}
    <SheetPrimitive.Content
      ref={ref}
      data-floating-drawer-panel={floating ? "" : undefined}
      className={cn(
        floating
          ? cn(floatingBase, floatingSide[side === "left" ? "left" : "right"])
          : sheetVariants({ side }),
        className,
      )}
      {...props}
    >
      {children}
      {!hideClose && (
        <SheetPrimitive.Close className="absolute right-8 top-6 rounded-[50%] transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none p-[5px] " style={{ background: 'var(--secondary-color)' }} >
          <X className="h-4 w-4 text-[#fff]" />
          <span className="sr-only">Close</span>
        </SheetPrimitive.Close>
      )}
    </SheetPrimitive.Content>
  </SheetPortal>
))
SheetContent.displayName = SheetPrimitive.Content.displayName

const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-2 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
SheetHeader.displayName = "SheetHeader"

const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
)
SheetFooter.displayName = "SheetFooter"

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold text-foreground", className)}
    {...props}
  />
))
SheetTitle.displayName = SheetPrimitive.Title.displayName

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
SheetDescription.displayName = SheetPrimitive.Description.displayName

export {
  Sheet, SheetClose,
  SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetOverlay, SheetPortal, SheetTitle, SheetTrigger
}

